import React, { useContext, useMemo } from 'react';

import {
  sortWithNullOrUndefinedValues,
  sortBooleanWithNullOrUndefinedValues,
} from 'Internals/sort';
import { withSeparators } from 'Internals/utils';
import { getSumQueuesMemoized } from '../utils/flows.utils';
import { getPathState, getColorClassNamesFromState } from 'Internals/graph';

import { SubscriptionContext } from '../pages/Subscription.page';

import { getSortValues } from '../components/filter-table/FilterTable';
import { PathRenderer } from '../components/path-renderer/PathRenderer.view';
import { LastRunState } from '../components/last-run-state/LastRunState.view';
import { IsDirectState } from '../components/is-direct-state/IsDirectState.view';
import PipeLink from '../components/common/Links/PipeLink';
import SystemLink from '../components/common/Links/SystemLink';
import EndpointTypeLink from '../components/common/Links/EndpointTypeLink';

import type { PipeMap } from 'src/types/pipes.types';

export const useFlowsListColumns = (pipes: PipeMap) => {
  const columns = useMemo(() => {
    return [
      {
        Header: 'Path',
        fixed: true,
        id: 'flows-path',
        width: 300,
        accessor: 'path',
        Cell: ({ cell: { value } }) => {
          const subId = useContext(SubscriptionContext);
          return <PathRenderer path={value} subId={subId} />;
        },
        sortType: getSortValues((a, b) => {
          return sortWithNullOrUndefinedValues(a[0], b[0]);
        }),
      },
      {
        Header: 'From',
        id: 'flows-from',
        accessor: 'from',
        Cell: ({ cell: { value } }) => <PipeLink pipeId={value} />,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'To',
        id: 'flows-to',
        accessor: 'to',
        Cell: ({ cell: { value } }) => <PipeLink pipeId={value} />,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'System',
        id: 'flows-system',
        accessor: 'systemId',
        Cell: ({ cell: { value } }) => <SystemLink systemId={value} />,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Endpoint type',
        id: 'flows-endpointtype',
        accessor: (f) => f,
        Cell: ({ cell: { value } }) => (
          <EndpointTypeLink type={value.endpointType} direction={value.direction} />
        ),
        sortType: getSortValues((a, b) => {
          const endpointTypeA = a.endpointType;
          const endpointTypeB = b.endpointType;
          return sortWithNullOrUndefinedValues(endpointTypeA, endpointTypeB);
        }),
      },
      {
        Header: 'Direction',
        id: 'flows-direction',
        accessor: 'direction',
        width: 100,
        Cell: ({ cell: { value } }) => <span title={value}>{value}</span>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Direct',
        id: 'flows-isdirect',
        accessor: 'direct',
        width: 75,
        Cell: ({ cell: { value } }) => <IsDirectState isDirect={value} />,
        sortType: getSortValues((a, b) => {
          const aBool = a === 'Yes';
          const bBool = b === 'Yes';

          return sortBooleanWithNullOrUndefinedValues(aBool, bBool);
        }),
      },
      {
        Header: 'Queues',
        id: 'flows-queues',
        accessor: (f) => getSumQueuesMemoized(f.path, pipes),
        align: 'right',
        Cell: ({ cell: { value } }) => <div>{withSeparators(value)}</div>,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Last run',
        id: 'flows-lastRun',
        accessor: 'lastRunStatusRaw',
        align: 'center',
        Cell: ({ cell: { value } }) => <LastRunState lastRun={value} />,
        sortType: getSortValues(sortBooleanWithNullOrUndefinedValues),
      },
      {
        Header: 'Status',
        id: 'flows-status',
        accessor: (f) => getPathState(f.path, pipes),
        align: 'center',
        Cell: ({ cell: { value } }) => {
          const state = value ? value : 'Unknown';
          const className = getColorClassNamesFromState(state);

          return (
            <span title={`At least one pipe in flow is in state: ${state}`} className={className}>
              {state}
            </span>
          );
        },
      },
    ];
  }, []);

  return columns;
};
